import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  FacebookIntegration,
  GoHighLevelIntegration,
  GoogleDriveIntegration,
  GoogleIntegration,
  GoogleBusinessProfileIntegration,
  InstagramIntegration,
  LinkedInIntegration,
  MailchimpIntegration,
  MetaAdsIntegration,
  ShopifyIntegration,
  TikTokIntegration,
  WixIntegration,
  WordPressIntegration,
  XIntegration,
  YouTubeIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import PlaceholderAvatar from './PlaceholderAvatar.png'
import { absurd } from 'types'

export const GoogleLogoFallback = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path d="M18.2813 10.1841C18.2813 9.50325 18.2249 9.00643 18.1029 8.49121H10.1699V11.5641H14.8264C14.7326 12.3278 14.2256 13.4778 13.099 14.2506L13.0832 14.3535L15.5915 16.2578L15.7653 16.2747C17.3612 14.8303 18.2813 12.705 18.2813 10.1841Z" fill="#4285F4"/><path d="M10.1694 18.2804C12.4507 18.2804 14.3659 17.5444 15.7647 16.2747L13.0985 14.2506C12.385 14.7382 11.4274 15.0786 10.1694 15.0786C7.93505 15.0786 6.03867 13.6342 5.36266 11.6377L5.26357 11.6459L2.65545 13.6241L2.62134 13.717C4.01077 16.4219 6.86476 18.2804 10.1694 18.2804Z" fill="#34A853"/><path d="M5.36303 11.6376C5.18465 11.1224 5.08142 10.5703 5.08142 9.99995C5.08142 9.42949 5.18466 8.87749 5.35364 8.36227L5.34892 8.25254L2.7081 6.24268L2.6217 6.28295C2.04905 7.40541 1.72046 8.66589 1.72046 9.99995C1.72046 11.334 2.04905 12.5944 2.6217 13.7169L5.36303 11.6376Z" fill="#FBBC05"/><path d="M10.1695 4.92144C11.756 4.92144 12.8263 5.59307 13.4365 6.15433L15.8211 3.87262C14.3566 2.53857 12.4508 1.71973 10.1695 1.71973C6.86479 1.71973 4.01077 3.57819 2.62134 6.28309L5.35329 8.36241C6.03869 6.36592 7.93508 4.92144 10.1695 4.92144Z" fill="#EB4335"/></svg>',
)}`

const facebookIntegrationDetails = (integration: FacebookIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.picture?.data?.url || PlaceholderAvatar
  const username = name

  return { name, picture, username }
}

const instagramIntegrationDetails = (integration: InstagramIntegration) => {
  const name = integration.additional_data?.username || integration.name
  const picture = integration.picture || integration.additional_data?.profile_picture_url || PlaceholderAvatar
  const username = integration.additional_data?.username

  return { name, picture, username }
}

const linkedinIntegrationDetails = (integration: LinkedInIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.picture || PlaceholderAvatar
  const username = integration.additional_data?.email || integration.name

  return { name, picture, username }
}

const xIntegrationDetails = (integration: XIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.profile_image_url || PlaceholderAvatar
  const username = integration.additional_data?.username

  return { name, picture, username }
}

const wordpressIntegrationDetails = (integration: WordPressIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.url || ''

  return { name, picture, username }
}

const tiktokIntegrationDetails = (integration: TikTokIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.username || ''

  return { name, picture, username }
}

const youtubeIntegrationDetails = (integration: YouTubeIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.username || ''

  return { name, picture, username }
}

const googleIntegrationDetails = (integration: GoogleIntegration) => {
  const { default_uri } = integration.additional_data || {}
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = default_uri || ''

  return { name, picture, username }
}

const metaAdsIntegrationDetails = (integration: MetaAdsIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.name

  return { name, picture, username }
}

const goHighLevelIntegrationDetails = (integration: GoHighLevelIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.user_name || ''

  return { name, picture, username }
}

interface MailchimpExtendedData {
  username?: string
  account_name?: string
  avatar_url?: string
}

const mailchimpIntegrationDetails = (integration: MailchimpIntegration) => {
  const extendedData = integration.additional_data as MailchimpExtendedData
  const username = extendedData?.username || integration.name
  return { name: username, picture: integration.picture || PlaceholderAvatar, username }
}

export const getIntegrationDetails = (integration?: ExternalIntegration, defaultName = 'Account Not Connected') => {
  if (!integration) {
    return { name: defaultName, picture: PlaceholderAvatar, username: 'youraccount' }
  }

  const { provider } = integration
  switch (provider) {
    case ExternalIntegrationEnum.WordPress:
      return wordpressIntegrationDetails(integration as WordPressIntegration)
    case ExternalIntegrationEnum.Facebook:
      return facebookIntegrationDetails(integration as FacebookIntegration)
    case ExternalIntegrationEnum.Instagram:
      return instagramIntegrationDetails(integration as InstagramIntegration)
    case ExternalIntegrationEnum.LinkedIn:
      return linkedinIntegrationDetails(integration as LinkedInIntegration)
    case ExternalIntegrationEnum.X:
      return xIntegrationDetails(integration as XIntegration)
    case ExternalIntegrationEnum.TikTok:
      return tiktokIntegrationDetails(integration as TikTokIntegration)
    case ExternalIntegrationEnum.YouTube:
      return youtubeIntegrationDetails(integration as YouTubeIntegration)
    case ExternalIntegrationEnum.Mailchimp:
      return mailchimpIntegrationDetails(integration as MailchimpIntegration)
    case ExternalIntegrationEnum.Google:
      return googleIntegrationDetails(integration as GoogleIntegration)
    case ExternalIntegrationEnum.GoogleBusinessProfile:
      return {
        name: (integration as GoogleBusinessProfileIntegration).name,
        picture: (integration as GoogleBusinessProfileIntegration).picture || GoogleLogoFallback,
        username: (integration as GoogleBusinessProfileIntegration).name,
      }
    case ExternalIntegrationEnum.GoogleDrive:
      return {
        name: (integration as GoogleDriveIntegration).name,
        picture: PlaceholderAvatar,
        username: (integration as GoogleDriveIntegration).name,
      }
    case ExternalIntegrationEnum.MetaAds:
      return metaAdsIntegrationDetails(integration as MetaAdsIntegration)
    case ExternalIntegrationEnum.GoogleAds:
      return {
        name: integration.name,
        picture: PlaceholderAvatar,
        username: integration.name,
      }
    case ExternalIntegrationEnum.GoHighLevel:
      return goHighLevelIntegrationDetails(integration as GoHighLevelIntegration)
    case ExternalIntegrationEnum.Wix:
      return {
        name: (integration as WixIntegration).name,
        picture: PlaceholderAvatar,
        username:
          (integration as WixIntegration).additional_data?.profile?.site_display_name ||
          (integration as WixIntegration).name,
      }
    case ExternalIntegrationEnum.Shopify:
      return {
        name: (integration as ShopifyIntegration).name,
        picture: PlaceholderAvatar,
        username: (integration as ShopifyIntegration).additional_data?.shop_domain || 'shopify',
      }
    default:
      absurd(provider)
      return { name: 'Account Not Connected', picture: PlaceholderAvatar, username: 'youraccount' }
  }
}
